// PreReservationNotifier.js
// Escucha eventos `new_pre_reservation` por Socket.IO y muestra un pop-up al
// staff del hotel cuando llega una nueva prereserva (desde el web o el bot de WhatsApp).

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  Snackbar,
  Alert,
  Slide,
} from '@mui/material';
import {
  IconBell,
  IconBrandWhatsapp,
  IconWorld,
  IconUser,
  IconCalendarEvent,
  IconBed,
  IconCurrencyDollar,
  IconTicket,
} from '@tabler/icons';
import { io as socketIO } from 'socket.io-client';
import { constant } from 'views/constant';

const SlideTransition = (props) => <Slide {...props} direction="left" />;

const getWsBaseUrl = () => {
  let url = constant.baseUrl || '';
  if (url.endsWith('/api/')) url = url.substring(0, url.length - 5);
  else if (url.endsWith('/api')) url = url.substring(0, url.length - 4);
  return url;
};

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleString();
  } catch (e) {
    return String(d || '');
  }
};

const PreReservationNotifier = () => {
  const navigate = useNavigate();
  const socketRef = useRef(null);

  // Cola de notificaciones recibidas (mostramos una a la vez en el Dialog principal)
  const [queue, setQueue] = useState([]);
  const [currentNotif, setCurrentNotif] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  // 🛎️ Notificación global de "cliente solicita recepción" (visible en TODO el PMS)
  const [receptionAlert, setReceptionAlert] = useState(null); // { phoneNumber, customerName }

  const hotelDataStr =
    typeof window !== 'undefined' ? localStorage.getItem('hotelData') : null;
  let hotelId = null;
  if (hotelDataStr) {
    try {
      const parsed = JSON.parse(hotelDataStr);
      hotelId = parsed?.hotelId || parsed?._id || null;
    } catch (e) {
      // ignore
    }
  }

  const playBeep = useCallback(() => {
    try {
      // Beep generado en cliente para no depender de ningún asset
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.25);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.45);
    } catch (e) {
      // ignore (algunos navegadores bloquean audio sin gesto del usuario)
    }
  }, []);

  useEffect(() => {
    if (!hotelId) return undefined;

    const wsUrl = getWsBaseUrl();
    console.log('[PreReservationNotifier] Conectando a', wsUrl, 'hotelId:', hotelId);

    const socket = socketIO(wsUrl, {
      transports: ['websocket', 'polling'],
      // El servidor deriva la sala de este token; el hotelId que se manda en
      // join_hotel_room queda solo por compatibilidad.
      auth: { token: localStorage.getItem('token') || sessionStorage.getItem('token') },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[PreReservationNotifier] ✅ Conectado, uniendo a sala hotel_' + hotelId);
      socket.emit('join_hotel_room', hotelId);
    });

    socket.on('connect_error', (err) => {
      console.warn('[PreReservationNotifier] ❌ connect_error:', err?.message || err);
    });

    socket.on('new_pre_reservation', (data) => {
      console.log('[PreReservationNotifier] 📨 new_pre_reservation', data);
      // Pequeño toast inmediato
      setToastMsg(
        `Nueva prereserva: ${data?.firstName || ''} ${data?.lastName || ''}`.trim() ||
          'Nueva prereserva recibida'
      );
      setToastOpen(true);
      playBeep();

      setQueue((prev) => [...prev, data]);
    });

    // 🛎️ Cliente de WhatsApp pidió hablar con recepción.
    // Este componente vive en MainLayout, así que la alerta llega aunque el
    // recepcionista NO esté en la página de Conversaciones.
    socket.on('reception_requested', (data) => {
      console.log('[PreReservationNotifier] 🛎️ reception_requested', data);
      // La página de Conversaciones ya muestra su propia alerta in-situ
      if (window.location.pathname.includes('/dashboard/conversations')) return;
      setReceptionAlert({
        phoneNumber: data?.phoneNumber,
        customerName: data?.customerName || data?.phoneNumber || 'Un cliente',
        replayed: Boolean(data?.replayed),
      });
      playBeep();
    });

    return () => {
      try {
        socket.disconnect();
      } catch (e) {
        // ignore
      }
    };
  }, [hotelId, playBeep]);

  // Si no hay nada visible y hay algo en la cola, mostrar el siguiente
  useEffect(() => {
    if (!currentNotif && queue.length > 0) {
      setCurrentNotif(queue[0]);
      setQueue((prev) => prev.slice(1));
    }
  }, [queue, currentNotif]);

  const handleClose = () => setCurrentNotif(null);

  const handleViewDetail = () => {
    setCurrentNotif(null);
    navigate('/dashboard/pre-reservations');
  };

  if (!hotelId) return null;

  const channelIcon =
    currentNotif?.channel === 'whatsapp' ? (
      <IconBrandWhatsapp size={20} />
    ) : (
      <IconWorld size={20} />
    );
  const channelLabel =
    currentNotif?.channel === 'whatsapp' ? 'WhatsApp' : 'Página web';

  return (
    <>
      {/* Toast pequeño que aparece de inmediato */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          severity="info"
          icon={<IconBell size={20} />}
          onClose={() => setToastOpen(false)}
          variant="filled"
        >
          {toastMsg}
        </Alert>
      </Snackbar>

      {/* 🛎️ Alerta global: cliente solicita hablar con recepción */}
      <Snackbar
        open={Boolean(receptionAlert)}
        autoHideDuration={15000}
        onClose={() => setReceptionAlert(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={SlideTransition}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="warning"
          variant="filled"
          icon={<IconBrandWhatsapp size={20} />}
          onClose={() => setReceptionAlert(null)}
          sx={{ cursor: 'pointer', fontWeight: 500 }}
          onClick={() => {
            setReceptionAlert(null);
            navigate('/dashboard/conversations');
          }}
        >
          🛎️ <strong>{receptionAlert?.customerName}</strong>{' '}
          {receptionAlert?.replayed
            ? 'sigue esperando a recepción — toca para abrir el chat'
            : 'solicita hablar con recepción — toca para abrir el chat'}
        </Alert>
      </Snackbar>

      {/* Pop-up principal */}
      <Dialog
        open={Boolean(currentNotif)}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
          }}
        >
          <IconBell size={22} />
          <span>¡Nueva prereserva recibida!</span>
        </DialogTitle>

        <DialogContent dividers>
          {currentNotif && (
            <Stack spacing={1.2} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconUser size={18} />
                <Typography variant="body1">
                  <strong>
                    {currentNotif.firstName} {currentNotif.lastName}
                  </strong>
                </Typography>
                <Chip
                  size="small"
                  icon={channelIcon}
                  label={channelLabel}
                  color={currentNotif.channel === 'whatsapp' ? 'success' : 'info'}
                  sx={{ ml: 'auto' }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconCalendarEvent size={18} />
                <Typography variant="body2">
                  <strong>Entrada:</strong> {formatDate(currentNotif.checkInDate)}
                </Typography>
              </Box>

              {currentNotif.checkOutDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconCalendarEvent size={18} />
                  <Typography variant="body2">
                    <strong>Salida:</strong> {formatDate(currentNotif.checkOutDate)}
                  </Typography>
                </Box>
              )}

              {currentNotif.roomTypeName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconBed size={18} />
                  <Typography variant="body2">
                    <strong>Habitación:</strong> {currentNotif.roomTypeName}
                  </Typography>
                </Box>
              )}

              {currentNotif.price != null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconCurrencyDollar size={18} />
                  <Typography variant="body2">
                    <strong>Precio:</strong> ${currentNotif.price}
                  </Typography>
                </Box>
              )}

              {currentNotif.promoCode && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconTicket size={18} />
                  <Typography variant="body2">
                    <strong>Código de promoción:</strong>
                  </Typography>
                  <Chip
                    size="small"
                    label={currentNotif.promoCode}
                    color="success"
                  />
                </Box>
              )}

              <Box sx={{ mt: 1 }}>
                <Chip
                  label={
                    currentNotif.stayType === 'noche'
                      ? 'Noche completa'
                      : currentNotif.stayType === 'parcial'
                      ? 'Tiempo parcial'
                      : currentNotif.stayType
                  }
                  size="small"
                  color={currentNotif.stayType === 'noche' ? 'primary' : 'secondary'}
                />
                {queue.length > 0 && (
                  <Chip
                    size="small"
                    label={`+${queue.length} en cola`}
                    sx={{ ml: 1 }}
                    color="warning"
                  />
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cerrar
          </Button>
          <Button
            onClick={handleViewDetail}
            variant="contained"
            color="primary"
          >
            Ver prereserva
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PreReservationNotifier;
